import { Dictionary, NumericDictionary } from 'lodash'
import { CategoryTreeItem } from './categories'
import { Coupon } from './coupon'
import { CouponSector } from './couponSector'
import { FileLink, SadukaImageFile } from './file'
import { OfferTag } from './offerTag'
import { SadukaResponseList } from './saduka'
import { TrafficTypeItem } from './trafficTypes'
import { Tool } from './tools'

export interface OfferFavicon {
  source: string
  fileName: string
  preview: string
}

export interface OfferIcons {
  favicon_16: FileLink,
  favicon_orig: FileLink,
  favicon_svg: FileLink,
}

export interface Offer {
  id: number
  name: string
  translated_name: string
  descriptions: number[]
  category_level2: number
  kpi: null
  kpi_en: null
  trust_level_id: number
  is_active: boolean
  added_at: string
  updated_at: string
  is_deleted: boolean
  status: number
  text: string
  text_language_id: number | null
  involved_tender: number
  shop_id: number
  autorun: number
  lead_notice: number
  date_start: string | null
  date_stop: string | null
  site_url: string
  favicon: SadukaImageFile[]
  icons: OfferIcons
  commission_data: {
    amount?: {
      min: number
      max: number
    }
    percent?: {
      min: number
      max: number
    }
    currency_id: number
  }
  action_type_direction_id: number
  platform: null
  text_en: string
  screen: [],
  no_screen_update: boolean
  show_in_retargeting: boolean
  type_link_custom: null,
  go_url_rules: null,
  approval_time: number
  payment_time: number
  sc_cookie_lifetime: number
  back_link_id: null,
  site_url_is_backlink: boolean
  click_domain_id: number
  currency_id: number
  stop_reason_id: number | null,
  is_stop_temporary: boolean,
  advertiser: number
  campaign_id: number
  manager_id: number
  account_manager_id: number
  cookie_lifetime: number
  business_category: number,
  regions: number[],
  tools: [],
  has_coupon: boolean
  is_deeplink_enabled: boolean,
  is_google_ads_enabled: boolean,
  is_microsoft_ads_enabled: boolean,
  tags: OfferTag['code'][],
  subscription_status: number
  planned_start_date: string | null,
  startDate: number
}

export interface OfferStat {
  ar: number
  commission_approved_for_three_months: number
  cr: number
  cr_is_expected: boolean
  currency_code: string
  ecpc: number
  ecpl: number
  leadbased_is_expected: boolean
  offer_id: number
  original_currency_code: string
  original_ecpc: number
  original_ecpl: number
}

export interface Region {
  id: number,
  name: string,
  code: string,
  items: Region[] | null,
}

export interface OfferStatReducer {
  [key: number]: OfferStat
}

type MetaData = {
  total: number,
  limit: number,
  page: number,
  withFilters: boolean
}

export interface OffersReducer {
  offers: {
    offersById: { [index: string]: Offer };
    offerFavoritesByOfferId: { [index: number]: boolean };
    offerFavoritesStateByOfferId: { [index: number]: 'ready' | 'loading' | 'updating' | null };

    meta: MetaData
    metaCoupons: MetaData

    offersGlobalTotal: number
    favoriteOffersGlobalTotal: number

    isLoadingOffers: boolean
    isLoadingCoupons: boolean

    isSuggestFormSending: boolean
    isSuggestFormSent: boolean
    suggestFormError: boolean | { [index: string]: string }

    view: 'list' | 'table'

    regionsById: { [index: string]: Region }

    action_type_direction: string[]
    action_type_directionById: Dictionary<ActionTypeDirection>

    platform: number[]
    platformById: NumericDictionary<Platform>

    subscription_status: SubscriptionStatus[]
    subscription_status_byId: Dictionary<SubscriptionStatus>

    coupon_offer: number[]
    couponOffersById: Dictionary<Offer>

    categoriesById: Dictionary<CategoryTreeItem>
    coupon_categoriesById: Dictionary<CategoryTreeItem>
    trafficTypesById: Dictionary<TrafficTypeItem>

    // @deprecated. use entity reducer
    tool_id: Tool[]
    toolsById: Dictionary<Tool>


    promo_type: PromoActionType[]
    promoActionTypesById: Dictionary<PromoActionType>

    coupon_status: PromoCouponStatus[],
    promoCouponStatusesById: Dictionary<PromoCouponStatus>

    couponScopes: PromoCouponScope[],
    promoCouponScopesById: Dictionary<PromoCouponScope>

    sectoring: CouponSector[],

    selectedFilters: {
      name: string
    }

    filterTotal: {
      isLoading: boolean
      requestFilterTotal: boolean
      total: number
    },

    couponsById: Dictionary<Coupon>
  },
  offerStatByOfferId: OfferStatReducer,
}

export type OffersList = SadukaResponseList<'offer', Offer[]>
export type OffersListPartial = SadukaResponseList<'offer', Partial<Offer>[]>

export type OfferTrafficType = {
  id: number,
  offer_id: number,
  name: string,
  traffic_type_id: number,
  note: string,
  value: number
}

export type OfferTrafficTypes = SadukaResponseList<'offerTrafficType', OfferTrafficType[]>

export type ActionTypeDirection = {
  id: string,
  name: string,
  title: string,
  value: string,
  icon: string
}

export type Platform = {
  value: number,
  icon: string,
  title: string,
  name: string
}

export type SubscriptionStatus = {
  id: number,
  name: string,
  status_name: string,
  value: number,
  title: string,
}

export type PromoActionType = {
  id: number,
  name: string,
  title: string,
  value: number,
}

export type PromoCouponStatus = {
  id: number
  name: string
  description: string
  is_default: string
  title: string
  value: number
  default: boolean
}

export type PromoCouponScope = {
  id: number,
  name: string,
  title: string,
  value: number,
}

export type OfferTarget = {
  id: number,
  text: string,
  offer: number,
  target_url: string,
  webmaster_commission: number,
  epa_percent: number,
  per_sales_show: number,
  per_sales_show_max: number,
  currency_id: number,
  sort: number,
  show_percent: boolean,
}

export enum OfferConnectRequestStatusId {
  InProgress = 1,
  Approved = 2,
  Rejected = 3,
}
